import React, { useState, useMemo } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Search,
    SlidersHorizontal,
    Download,
    X,
    Check,
    ChevronsUpDown,
    Trash2,
    MoreHorizontal
} from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuCheckboxItem
} from './dropdown-menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from './popover';

/**
 * Enterprise Data Table
 * Features: Sorting, Filtering, Pagination, Selection, Column Visibility, Empty/Loading/Error States
 */
export const DataTable = ({
    columns,
    data,
    keyField = 'id',
    isLoading = false,
    error = null,
    onRetry,
    onSelectionChange,
    bulkActions,
    actions,
    searchPlaceholder = "Search...",
    emptyState,
    showColumnToggle = true,
    showExport = true,
    showSearch = true,
    defaultPageSize = 10,
    pageSizeOptions = [10, 25, 50, 100]
}) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [visibleColumns, setVisibleColumns] = useState(
        columns.reduce((acc, col) => ({ ...acc, [col.accessor]: true }), {})
    );

    // Sorting Logic
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Selection Logic
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = new Set(paginatedData.map(item => item[keyField]));
            setSelectedIds(allIds);
            onSelectionChange?.(Array.from(allIds));
        } else {
            setSelectedIds(new Set());
            onSelectionChange?.([]);
        }
    };

    const handleSelectRow = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
        onSelectionChange?.(Array.from(newSelected));
    };

    const clearSelection = () => {
        setSelectedIds(new Set());
        onSelectionChange?.([]);
    };

    // Column filter change
    const handleColumnFilter = (accessor, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [accessor]: value
        }));
        setCurrentPage(1);
    };

    const clearColumnFilter = (accessor) => {
        setColumnFilters(prev => {
            const newFilters = { ...prev };
            delete newFilters[accessor];
            return newFilters;
        });
    };

    // Toggle column visibility
    const toggleColumn = (accessor) => {
        setVisibleColumns(prev => ({
            ...prev,
            [accessor]: !prev[accessor]
        }));
    };

    // Filter & Sort Data
    const processedData = useMemo(() => {
        let filtered = [...data];

        // Global search
        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                Object.values(item).some(val =>
                    String(val).toLowerCase().includes(lowerQuery)
                )
            );
        }

        // Column filters
        Object.entries(columnFilters).forEach(([accessor, filterValue]) => {
            if (filterValue) {
                filtered = filtered.filter(item =>
                    String(item[accessor]).toLowerCase().includes(filterValue.toLowerCase())
                );
            }
        });

        // Sorting
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return filtered;
    }, [data, sortConfig, searchQuery, columnFilters]);

    // Pagination
    const totalPages = Math.ceil(processedData.length / pageSize);
    const paginatedData = processedData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    // Reset page when data changes significantly
    React.useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const visibleColumnList = columns.filter(col => visibleColumns[col.accessor]);
    const activeFilterCount = Object.keys(columnFilters).length;

    // Export handler
    const handleExport = () => {
        const headers = visibleColumnList.map(col => col.header).join(',');
        const rows = processedData.map(row =>
            visibleColumnList.map(col => {
                const val = row[col.accessor];
                return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
            }).join(',')
        );
        const csv = [headers, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'export.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    // Error state
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center w-full py-16 px-4 bg-red-50/50 rounded-md border border-red-100">
                <div className="bg-red-100 p-3 rounded-full mb-4">
                    <X className="h-5 w-5 text-red-600" />
                </div>
                <p className="text-sm font-medium text-slate-900 mb-1">Failed to load data</p>
                <p className="text-sm text-slate-500 text-center max-w-sm mb-4">{error}</p>
                {onRetry && (
                    <Button onClick={onRetry} variant="outline" size="sm">
                        Try again
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className="w-full space-y-3">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-1">
                    {showSearch && (
                        <div className="relative w-full max-w-xs">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                            <Input
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="pl-9 h-9"
                            />
                        </div>
                    )}
                    {activeFilterCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setColumnFilters({})}
                            className="h-9 text-slate-500"
                        >
                            Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
                            <X className="ml-1 h-3 w-3" />
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {actions}
                    {showColumnToggle && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="h-9 gap-1.5">
                                    <SlidersHorizontal className="h-3.5 w-3.5" />
                                    Columns
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                {columns.map(col => (
                                    <DropdownMenuCheckboxItem
                                        key={col.accessor}
                                        checked={visibleColumns[col.accessor]}
                                        onCheckedChange={() => toggleColumn(col.accessor)}
                                    >
                                        {col.header}
                                    </DropdownMenuCheckboxItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    {showExport && (
                        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={handleExport}>
                            <Download className="h-3.5 w-3.5" />
                            Export
                        </Button>
                    )}
                </div>
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
                <div className="flex items-center justify-between px-3 py-2 bg-slate-100 border border-slate-200 rounded-md">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-slate-700">
                            {selectedIds.size} selected
                        </span>
                        <Button variant="ghost" size="sm" onClick={clearSelection} className="h-7 text-xs">
                            Clear
                        </Button>
                    </div>
                    {bulkActions && (
                        <div className="flex items-center gap-2">
                            {bulkActions}
                        </div>
                    )}
                </div>
            )}

            {/* Table Container */}
            <div className="rounded-md border border-gray-200 overflow-hidden bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-slate-50 border-b border-gray-200">
                            <tr>
                                <th className="w-10 px-3 py-3">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-slate-900 focus:ring-slate-500 focus:ring-offset-0"
                                        checked={selectedIds.size === paginatedData.length && paginatedData.length > 0}
                                        ref={el => {
                                            if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < paginatedData.length;
                                        }}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                {visibleColumnList.map((col) => (
                                    <th key={col.accessor} className="px-3 py-3">
                                        <div className="flex flex-col gap-1">
                                            <div
                                                className={`flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase tracking-wide ${col.sortable ? 'cursor-pointer hover:text-slate-700' : ''}`}
                                                onClick={() => col.sortable && handleSort(col.accessor)}
                                            >
                                                {col.header}
                                                {col.sortable && (
                                                    sortConfig.key === col.accessor
                                                        ? sortConfig.direction === 'asc'
                                                            ? <ArrowUp className="h-3 w-3" />
                                                            : <ArrowDown className="h-3 w-3" />
                                                        : <ArrowUpDown className="h-3 w-3 opacity-40" />
                                                )}
                                            </div>
                                            {col.filterable && (
                                                <div className="relative">
                                                    <Input
                                                        placeholder={`Filter...`}
                                                        value={columnFilters[col.accessor] || ''}
                                                        onChange={(e) => handleColumnFilter(col.accessor, e.target.value)}
                                                        className="h-7 text-xs pr-6"
                                                    />
                                                    {columnFilters[col.accessor] && (
                                                        <button
                                                            onClick={() => clearColumnFilter(col.accessor)}
                                                            className="absolute right-1.5 top-1.5 text-slate-400 hover:text-slate-600"
                                                        >
                                                            <X className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={visibleColumnList.length + 1} className="py-20 text-center">
                                        <div className="flex flex-col items-center justify-center gap-2">
                                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" />
                                            <p className="text-sm text-slate-500">Loading data...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedData.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColumnList.length + 1} className="py-16 text-center">
                                        {emptyState || (
                                            <div className="flex flex-col items-center justify-center gap-1">
                                                <p className="text-sm font-medium text-slate-900">No results</p>
                                                <p className="text-sm text-slate-500">
                                                    {searchQuery || activeFilterCount > 0
                                                        ? 'Try adjusting your search or filters'
                                                        : 'No data available'}
                                                </p>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                paginatedData.map((row, rowIndex) => (
                                    <tr
                                        key={row[keyField] || rowIndex}
                                        className={`hover:bg-slate-50 transition-colors ${selectedIds.has(row[keyField]) ? 'bg-slate-50' : ''}`}
                                    >
                                        <td className="px-3 py-3">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 text-slate-900 focus:ring-slate-500 focus:ring-offset-0"
                                                checked={selectedIds.has(row[keyField])}
                                                onChange={() => handleSelectRow(row[keyField])}
                                            />
                                        </td>
                                        {visibleColumnList.map((col) => (
                                            <td key={col.accessor} className="px-3 py-3 align-middle">
                                                {col.cell ? col.cell(row) : row[col.accessor]}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span>
                        {processedData.length === 0
                            ? '0 results'
                            : `${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, processedData.length)} of ${processedData.length}`}
                    </span>
                    <span className="text-slate-300">|</span>
                    <div className="flex items-center gap-1">
                        <span>Show</span>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className="h-7 px-1.5 border border-gray-200 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-slate-400"
                        >
                            {pageSizeOptions.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                        <span>per page</span>
                    </div>
                </div>
                {totalPages > 1 && (
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div className="flex items-center gap-1 px-2">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                return (
                                    <Button
                                        key={pageNum}
                                        variant={currentPage === pageNum ? "default" : "ghost"}
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setCurrentPage(pageNum)}
                                    >
                                        {pageNum}
                                    </Button>
                                );
                            })}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
